"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type { BlogPost } from "../lib/blog";

type NavItem = {
  title: string;
  href: string;
  children?: NavItem[];
  level?: number;
};

// 静态导航项目（非博客部分）
const staticNavItems: NavItem[] = [
  {
    title: "Home",
    href: "/",
  },
];

export function TableOfContents() {
  const pathname = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Close mobile menu when path changes
    setIsMobileMenuOpen(false);
  }, [pathname]);

  // 处理路径变化时的展开逻辑 - 使用静态数据
  useEffect(() => {
    // 自动展开当前路径的所有父级目录
    const pathsToExpand = new Set<string>();
    
    // 基于当前路径自动展开
    if (pathname.startsWith('/blog')) {
      pathsToExpand.add('/blog');
      
      // 根据路径结构自动展开父级
      const pathParts = pathname.split('/').filter(Boolean);
      let currentPath = '';
      for (const part of pathParts) {
        currentPath += `/${part}`;
        if (currentPath.startsWith('/blog')) {
          pathsToExpand.add(currentPath);
        }
      }
    }
    
    setExpandedItems(pathsToExpand);
  }, [pathname]);

  // 切换展开状态
  const toggleExpanded = useCallback((href: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(href)) {
        newSet.delete(href);
      } else {
        newSet.add(href);
      }
      return newSet;
    });
  }, []);

  // 构建静态导航结构（不依赖API）
  const allNavItems: NavItem[] = [
    ...staticNavItems,
    {
      title: "Blog",
      href: "/blog",
      children: [
        {
          title: "前端开发",
          href: "/blog/frontend",
          level: 0,
          children: [
            {
              title: "React 开发",
              href: "/blog/frontend/react",
              level: 1,
              children: [
                {
                  title: "React Hooks 详解",
                  href: "/blog/frontend/react/hooks",
                  level: 2
                }
              ]
            }
          ]
        },
        {
          title: "后端开发",
          href: "/blog/backend",
          level: 0,
          children: [
            {
              title: "Node.js 开发指南",
              href: "/blog/backend/nodejs",
              level: 1
            }
          ]
        },
        {
          title: "Next.js 完整教程",
          href: "/blog/next-js-tutorial",
          level: 0
        },
        {
          title: "文章1：示例博客文章",
          href: "/blog/article-1",
          level: 0
        }
      ]
    },
    {
      title: "Work",
      href: "/work",
    },
  ];

  // 递归渲染导航项目
  const renderNavItem = useCallback((item: NavItem, depth: number = 0) => {
    const isActive = pathname === item.href;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.href) || (depth === 0 && item.href === "/blog");
    const isParentOfActive = pathname.startsWith(item.href + '/') || pathname === item.href;

    return (
      <div key={item.href} className={`${depth > 0 ? 'ml-2' : ''}`}>
        <div className="flex items-center">
          <Link
            href={item.href}
            className={`flex-1 py-2 px-3 rounded-xl transition-all duration-200 ${
              isActive
                ? "bg-purple-100/50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 shadow-lg backdrop-blur-sm"
                : isParentOfActive
                ? "bg-purple-50/30 dark:bg-purple-900/20 text-purple-500 dark:text-purple-300"
                : "text-gray-900 dark:text-gray-100 hover:bg-white/40 dark:hover:bg-gray-800/40 hover:backdrop-blur-sm hover:shadow-md"
            } ${depth > 0 ? 'text-sm' : ''}`}
          >
            {item.title}
          </Link>
          
          {hasChildren && (
            <button
              onClick={(e) => {
                e.preventDefault();
                toggleExpanded(item.href);
              }}
              className={`ml-1 p-1 rounded-md transition-all duration-200 ${
                isActive || isParentOfActive
                  ? "text-purple-600 dark:text-purple-400 hover:bg-purple-100/50 dark:hover:bg-purple-900/30"
                  : "text-gray-500 dark:text-gray-400 hover:bg-white/40 dark:hover:bg-gray-800/40"
              }`}
              aria-label={isExpanded ? "收起" : "展开"}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className={`w-4 h-4 transition-transform duration-200 ${
                  isExpanded ? 'rotate-90' : ''
                }`}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>

        {hasChildren && isExpanded && (
          <div className={`mt-1 space-y-1 ${depth > 0 ? 'border-l-2 border-purple-200/50 dark:border-purple-700/50 ml-2 pl-2' : 'ml-4'}`}>
            {item.children!.map((child) => renderNavItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  }, [pathname, expandedItems, toggleExpanded]);

  return (
    <>
      {/* Mobile menu button */}
      <button
        className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-xl bg-white/80 dark:bg-gray-900/80 backdrop-blur-md shadow-lg border border-white/20 dark:border-gray-700/30"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-6 h-6 text-gray-700 dark:text-gray-300"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d={
              isMobileMenuOpen
                ? "M6 18L18 6M6 6l12 12"
                : "M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5"
            }
          />
        </svg>
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 h-full w-64 transform transition-transform duration-300 ease-in-out bg-white/70 dark:bg-gray-900/70 backdrop-blur-xl border-r border-white/30 dark:border-gray-700/30 ${
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <div className="p-4 h-full overflow-y-auto">
          <div className="mb-8 mt-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              <span className="anime-gradient-text">My Blog</span>
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              ✨ Explore content and articles
            </p>
          </div>

          <nav className="space-y-1">
            {allNavItems.map((item) => (
              <div key={item.href} className="mb-4">
                {renderNavItem(item)}
              </div>
            ))}
          </nav>
        </div>
      </aside>
    </>
  );
}
